import {
  FunctionField,
  List,
  TextField,
  SimpleList,
  Datagrid,
  useNotify,
  useDataProvider,
  ReferenceField,
  Title,
  useStore,
} from "react-admin";
import { EmptyList } from "server/components";
import { Box, Typography, Paper, useMediaQuery } from "@mui/material";
import { useEffect, useState } from "react";
import CustomFilter from "features/CustomFilter";
import { LockedCandidacy } from "./LockCandidacy";
import { CampaignSubTypes, CampaignTypes, MOBILE_BREAKPOINT } from "utils/constants";
import { candidateRequests } from "api/candidateApi";
import CampaignTopToolbar from "features/CampaignToolbar";
import { settings } from "app";
import { administrationRequests } from "api/administrationApi";
import getCandidaciesFilters from "./Filters";
import { CandidatesExporter } from "../XlsxExporter";
import { useNotifyError } from "utils/useNotifyError";
import { MobileListRow } from "features/MobileListRow";
import { getDisplayName } from "utils/helpers";

const getCampaignsLabel = (campaignSubType, campaignType) => {
  switch (campaignSubType) {
    case CampaignSubTypes.Regular:
      return "редовни кампании";
    case CampaignSubTypes.Preliminary:
      return "предварителни кампании";
    case CampaignSubTypes.Paid:
      return "платени кампании";
    case CampaignSubTypes.HospitalContract:
      return "кампании с договор с болница";
    default:
      if (campaignType === CampaignTypes.PreparatoryCourses) {
        return "подготвителна курсове";
      }
      return "";
  }
};

const ListCandidates = ({ campaignSubType, campaignType }) => {
  const notify = useNotify();
  const notifyError = useNotifyError();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const dataProvider = useDataProvider();

  const [choicesAll, setChoices] = useState([]);
  const STORE_KEY = campaignSubType
    ? `campaign-query-subtype-${campaignSubType}`
    : campaignType
      ? `campaign-query-type-${campaignType}`
      : "campaign-query";

  // filters drives the <List> and the gate (persisted via useStore);
  // campaignIds is the draft selection bound to CustomFilter, committed on "Провери".
  const [filters, setFilters] = useStore(STORE_KEY, null);
  const [campaignIds, setCampaignIds] = useState(filters?.campaignId ?? null);

  useEffect(() => {
    const fetchFor = async ({ subType, type }) => {
      const base = candidateRequests.getCampaignsSelect();
      const url = type ? `${base}?Type=${type}` : subType != null ? `${base}?SubType=${subType}` : base;
      const res = await dataProvider.customRequest(url);
      return res?.data ?? [];
    };
    (async () => {
      try {
        let data = await fetchFor({ subType: campaignSubType, type: campaignType });

        if ((!Array.isArray(data) || data.length === 0) && !campaignSubType && !campaignType) {
          data = await fetchFor({ subType: CampaignSubTypes.Regular });
        }

        setChoices(data);
        // Auto-select and show when exactly one active campaign exists.
        const activeCampaigns = data?.filter(x => x.isActiveRegistration === true);
        if (activeCampaigns.length === 1) {
          const id = activeCampaigns[0].id;
          setCampaignIds(id);
          setFilters({ campaignId: id });
        }
      } catch (error) {
        notifyError(error);
      }
    })();
  }, [STORE_KEY, campaignSubType, campaignType, dataProvider, notifyError, setFilters]);

  const searchParams = () => {
    if (campaignIds) {
      setFilters({ campaignId: campaignIds });
    } else {
      notify("Моля въведете кампания!", { type: "error" });
    }
  };
  const filtersToShow = getCandidaciesFilters();
  const titleHeader = `Кандидатури за ${getCampaignsLabel(campaignSubType, campaignType)}`;
  return (
    <>
      {!filters && <Title title={titleHeader} />}
      <CustomFilter
        title="Списък кампании"
        filters={[
          {
            label: "Кампания",
            value: campaignIds,
            setValue: setCampaignIds,
            type: "autocomplete",
            multiple: false,
            choices: choicesAll,
          },
        ]}
        cardWidth="700px"
        buttonLabel="Провери"
        buttonOnClick={searchParams}
      />
      {!filters && (
        <Paper>
          <Box mt={3}>
            <Typography
              variant="inherit"
              align="center"
              color={"secondary"}
            >
              Въведи данни за търсене на кандидатури.
            </Typography>
          </Box>
        </Paper>
      )}
      {filters && (
        <List
          actions={
            <CampaignTopToolbar
              campaignSubType={campaignSubType}
              campaignIds={filters.campaignId}
              isPaid={campaignSubType === CampaignSubTypes.Paid}
              exporter={CandidatesExporter}
            />
          }
          filters={filtersToShow}
          title={titleHeader}
          sx={settings.style.blockStyle}
          filter={{
            campaignId: filters.campaignId,
            ...(campaignSubType !== undefined ? { SubType: campaignSubType } : {}),
          }}
          empty={
            campaignSubType === CampaignSubTypes.Regular || campaignSubType === CampaignSubTypes.Paid ? (
              <CampaignTopToolbar
                campaignSubType={campaignSubType}
                campaignIds={filters.campaignId}
                empty
              />
            ) : (
              <EmptyList />
            )
          }
        >
          {isMobile ? (
            <SimpleList
              empty={<EmptyList />}
              linkType={false}
              sx={settings.style.mobileList}
              primaryText={record => (
                <div>
                  <MobileListRow fields={[{ label: "ID", value: record.id }]} />
                  <MobileListRow fields={[{ label: "Вх. номер", value: record.number }]} />
                </div>
              )}
              secondaryText={record => (
                <div>
                  <MobileListRow fields={[{ label: "Име", value: record.firstName }]} />
                  <MobileListRow fields={[{ label: "Презиме", value: record.secondName }]} />
                  <MobileListRow fields={[{ label: "Фамилия", value: record.lastName }]} />
                  <MobileListRow
                    fields={[
                      {
                        label: "1-во желание",
                        value: (
                          <ReferenceField
                            source="firstPrioritySpecialityId"
                            reference={administrationRequests.getSpecialitiesSelect()}
                          />
                        ),
                      },
                    ]}
                  />
                  <MobileListRow fields={[{ label: "Дата на кандидатурата", value: record.createdOnFormatted }]} />
                  <MobileListRow
                    fields={[
                      {
                        label: "Статус",
                        value: (
                          <>
                            <ReferenceField
                              source="stateId"
                              reference={candidateRequests.getCandidacieStatesSelect()}
                              label="Статус"
                            />
                            {record.stateNameBg}
                          </>
                        ),
                      },
                    ]}
                  />
                  <MobileListRow fields={[{ label: "Обработва се от", value: record.workerNames }]} />
                </div>
              )}
              tertiaryText={() => (
                <>
                  <LockedCandidacy />
                </>
              )}
            />
          ) : (
            <Datagrid
              bulkActionButtons={false}
              rowClick={false}
              sx={{ ...settings.style.firstExpandIconDatagrid, ...settings.style.compactDatagridCells }}
            >
              <TextField
                source="id"
                label="ID"
                sortable={false}
              />

              <TextField
                source="number"
                label="Вх. номер"
                sortable={false}
              />

              {/* One names column instead of three - saves width so the table fits a 14" screen. */}
              <FunctionField
                label="Имена"
                render={getDisplayName}
              />
              <ReferenceField
                source="firstPrioritySpecialityId"
                reference={administrationRequests.getSpecialitiesSelect()}
                label="1-во желание"
              />
              <TextField
                source="createdOnFormatted"
                label="Дата на кандидатурата"
                sortable={false}
              />
              <ReferenceField
                source="stateId"
                reference={candidateRequests.getCandidacieStatesSelect()}
                label="Статус"
              />
              <TextField
                label="Обработва се от"
                source="workerNames"
                sortable={false}
              />
              <LockedCandidacy />
            </Datagrid>
          )}
        </List>
      )}
    </>
  );
};
export default ListCandidates;
